'use client';

import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { logger } from '@/lib/platform/utils/logger';
import {
  useUserPreferences,
  type LocalePreference,
} from '@/lib/platform/hooks/use-user-preferences';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { DEMO_MODE } from '@/lib/platform/config/constants';

// Re-export for backwards compatibility
type Locale = LocalePreference;

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Use React Query for user preferences (with automatic localStorage persistence)
  const { updateLocale } = useUserPreferences();
  const { organization } = useOrganization();

  // Check if user is in demo mode
  const isDemo = organization?.id === DEMO_MODE.ORGANIZATION_ID;

  // TEMPORARY: Force English locale until Danish translation is complete
  // TODO: Remove this and restore: const locale = isDemo ? 'en' : preferences.locale;
  const locale: Locale = 'en';

  const setLocale = useCallback(
    async (newLocale: Locale) => {
      // Block locale change in demo mode
      if (isDemo) {
        logger.log('[Language] Locale change blocked in demo mode');
        return;
      }

      try {
        logger.log('[Language] Setting locale to:', newLocale);
        updateLocale(newLocale);
        // Database sync can be added later if needed
      } catch (err) {
        logger.error('Error updating language:', err);
      }
    },
    [updateLocale, isDemo]
  );

  const contextValue = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
